import React, { useState, useEffect, useRef } from "react";
import "../../../styles/SessionPage.css";
import logo from "../../../assets/icons/logo.svg";
import { FaMicrophoneSlash } from "react-icons/fa";

import { ReactComponent as VideoIcon } from "../../../assets/icons/video.svg";
import { ReactComponent as VoiceAudioIcon } from "../../../assets/icons/audiomic.svg";
import { ReactComponent as AiModelIcon } from "../../../assets/icons/aimodel.svg";
import { ReactComponent as EndCallIcon } from "../../../assets/icons/endcall.svg";
import { ReactComponent as VoiceModelIcon } from "../../../assets/icons/voicemodel.svg";

import { useNavigate } from "react-router-dom";
import { useWebSocketChat } from "../../../socket/useSocket";
import { AuthServices } from "../../auth/services/AuthServices.js";

const SessionPage = () => {
  const navigate = useNavigate();

  const [selectedExpert, setSelectedExpert] = useState(() => {
    return localStorage.getItem("selectedExpert") || "";
  });
  const [isMuted, setIsMuted] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showVideoConfirmModal, setShowVideoConfirmModal] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedModelType, setSelectedModelType] = useState(selectedExpert);

  const {
    socket,
    updatePatientId,
    sendAudio,
    audioChunksRef,
    setBotStatus,
    botStatus,
    updatePersona,
  } = useWebSocketChat({
    setCompleteAudio: (data) => handleAudioComplete(data),
    onStreamingStart: (data) => handleStreamingStart(data),
    onStreamingText: (data) => data,
    onStreamingAudio: (data) => handleStreamAudio(data),
    onStreamingComplete: (data) => handleStreamingComplete(data),
    onStreamingError: (data) => handleStreamError(data),
    onTranscription: () => {},
    onConversationHistory: () => {},
  });

  const [user, setUser] = useState();
  const audioRef = useRef(new Audio());
  const audioQueue = useRef([]);
  const isProcessingQueue = useRef(false);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const isRecordingRef = useRef(false);

  const handleAudioComplete = (data) => {};

  const handleStreamingStart = (data) => {};

  const handleModelSelect = (type) => {
    setSelectedModelType(type.target.innerText);
  };

  const handleSendSelectedModel = () => {
    setSelectedExpert(selectedModelType);
    localStorage.setItem("selectedExpert", selectedModelType);
    updatePersona(selectedModelType);
    setShowModelModal(false);

    if (socket && socket.readyState === 1) {
      socket.close();
    }

    window.location.reload();
  };

  const handleSetVoice = (voice) => {
    setSelectedVoice(voice.target.innerText);
  };

  const handleVoiceSelect = () => {
    let mappedVoice;

    switch (selectedVoice) {
      case "Nova – Female, Soft":
        mappedVoice = "nova";
        break;
      case "Alloy – Neutral":
        mappedVoice = "alloy";
        break;
      case "Echo – Male":
        mappedVoice = "echo";
        break;
      case "Fable – Female":
        mappedVoice = "fable";
        break;
      case "Onyx – Male, Deep":
        mappedVoice = "onyx";
        break;
      case "Shimmer – Female, Clear":
        mappedVoice = "shimmer";
        break;
      default:
        mappedVoice = "default";
    }

    localStorage.setItem("selectedVoice", mappedVoice);
    window.location.reload();

    setShowVoiceModal(false);
  };

  const handleStreamAudio = (data) => {
    if (!data || !data.audio) return;
    enqueueAudio(data.audio);
  };

  const handleStreamingComplete = (data) => {
    console.log("Bot finished responding.");
    setBotStatus(null);
    startRecording();
  };

  const enqueueAudio = (audioData) => {
    const blob = new Blob(
      [Uint8Array.from(atob(audioData), (c) => c.charCodeAt(0))],
      {
        type: "audio/mpeg",
      }
    );
    audioQueue.current.push(blob);
    if (!isProcessingQueue.current) processAudioQueue();
  };

  const processAudioQueue = async () => {
    const audio = audioRef.current;
    if (!audio) {
      console.warn("Audio element not ready yet");
      return;
    }

    if (audioQueue.current.length === 0) {
      isProcessingQueue.current = false;
      return;
    }

    isProcessingQueue.current = true;
    const blob = audioQueue.current.shift();
    const url = URL.createObjectURL(blob);

    try {
      audio.src = url;
      await audio.play();

      audio.onended = () => {
        URL.revokeObjectURL(url);
        processAudioQueue();
      };
    } catch (err) {
      console.error("Audio playback error:", err);
      URL.revokeObjectURL(url);
      isProcessingQueue.current = false;
    }
  };

  const startRecording = async () => {
    if (!audioRef.current?.paused) {
      console.log("Bot audio is still playing. Waiting to start recording...");
      setTimeout(() => {
        startRecording();
      }, 500);
      return;
    }
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      alert("Audio recording is not supported in this browser.");
      return;
    }

    setBotStatus("listening");
    console.log("Recording started...");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log("MediaRecorder stopped. Sending audio...");
        await sendAudio();
        isRecordingRef.current = false;
        stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
      };

      mediaRecorder.start();
      isRecordingRef.current = true;

      detectSilence(stream, 2000, 0.01, () => {
        stopRecording();
        isRecordingRef.current = false;
      });
    } catch (error) {
      console.error("Microphone error:", error);
      alert(`Microphone error: ${error.message}`);
    }
  };

  const handleStreamError = (data) => {};

  const detectSilence = (
    stream,
    silenceDuration = 2000,
    threshold = 0.01,
    onSilence = () => {}
  ) => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioCtx.createAnalyser();
    const source = audioCtx.createMediaStreamSource(stream);
    const data = new Uint8Array(analyser.fftSize);

    source.connect(analyser);

    let silenceStart = null;
    let startedTalking = false;

    const checkSilence = () => {
      analyser.getByteTimeDomainData(data);

      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const val = (data[i] - 128) / 128;
        sum += val * val;
      }
      const volume = Math.sqrt(sum / data.length);

      if (volume > threshold) {
        startedTalking = true;
        silenceStart = null;
      }

      if (startedTalking) {
        if (volume < threshold) {
          if (!silenceStart) silenceStart = performance.now();
          if (performance.now() - silenceStart > silenceDuration) {
            onSilence();
            audioCtx.close();
            return;
          }
        } else {
          silenceStart = null;
        }
      }

      requestAnimationFrame(checkSilence);
    };

    checkSilence();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
    }
  };

  const handleMuteToggle = () => {
    const audioTracks = streamRef.current?.getAudioTracks();
    if (audioTracks && audioTracks.length > 0) {
      const current = audioTracks[0];
      current.enabled = !current.enabled;
      setIsMuted(!isMuted);
    }
  };

  const handleEndCall = () => {
    setShowEndModal(true);
  };

  const fetchUserData = async () => {
    try {
      const response = await AuthServices.fetchUserData();
      const fetchedUser = response?.data?.data?.user;

      if (response.status !== 200) {
        socket?.close();
        localStorage.clear();
        navigate("/");
      }

      if (fetchedUser) {
        setUser(fetchedUser);
        localStorage.setItem("user", JSON.stringify(fetchedUser));

        if (fetchedUser?.patient_id) {
          updatePatientId(fetchedUser.patient_id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  const handleEndNow = () => {
    if (socket?.readyState === 1) {
      socket.close();
    }
    window.location.href = "/home";
  };

  useEffect(() => {
    if (socket?.readyState === 1 && user?.patient_id) {
      const initRecording = async () => {
        await startRecording();
      };
      initRecording();
    }
  }, [socket]);

  useEffect(() => {
    if (audioRef.current) {
      processAudioQueue();
    }
  }, []);

  useEffect(() => {
    const getUser = async () => {
      fetchUserData();
    };
    getUser();
  }, []);

  useEffect(() => {
    if (user?.patient_id) {
      updatePersona(selectedExpert);
    }
  }, [user]);

  return (
    <div className="session-container">
      <audio ref={audioRef} style={{ display: "none" }} />
      <div className="session-info">
        <img src={logo} alt="Bot Avatar" className="session-avatar" />
        <p>
          You are having a convo with our <strong>Prasha Sync</strong>{" "}
          {selectedModelType} AI model
        </p>
      </div>
      {botStatus && (
        <div className={`bot-indicator ${botStatus}`}>
          {botStatus === "listening" && "👂 Listening..."}
          {botStatus === "thinking" && "💭 Thinking..."}
        </div>
      )}

      <footer className="session-controls">
        <button className="circle-btn" onClick={handleMuteToggle}>
          <button className="circle-btn" onClick={handleMuteToggle}>
            {isMuted ? (
              <FaMicrophoneSlash color="#f00" size={20} />
            ) : (
              <VoiceAudioIcon width={20} height={20} />
            )}
          </button>
        </button>
        <button
          className="circle-btn"
          onClick={() => setShowVideoConfirmModal(true)}
        >
          <VideoIcon width={20} height={20} />
        </button>

        <button className="circle-btn" onClick={() => setShowModelModal(true)}>
          <AiModelIcon width={20} height={20} />
        </button>
        <button className="circle-btn" onClick={() => setShowVoiceModal(true)}>
          <VoiceModelIcon width={20} height={20} />
        </button>
        <div className="divider" />
        <button className="circle-btn end-call" onClick={handleEndCall}>
          <EndCallIcon width={20} height={20} />
        </button>
      </footer>
      {/* Model Selection Modal */}
      {showModelModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="modal-header-indicator" />
            <h3>Choose Your AI Model</h3>
            <ul className="voice-list">
              {["psychologist", "dietician"].map((model, index) => (
                <li
                  style={{
                    backgroundColor:
                      model === selectedModelType ? "#dfefff" : "",
                    cursor: "pointer",
                    borderRadius: "8px",
                    padding: "10px",
                    transition: "0.2s",
                  }}
                  key={index}
                  className="voice-item"
                  onClick={handleModelSelect}
                >
                  <span>{model}</span>
                </li>
              ))}
            </ul>
            <div className="modal-actions">
              <button
                className="cancel"
                onClick={() => setShowModelModal(false)}
              >
                Cancel
              </button>
              <button
                className="done"
                disabled={!selectedModelType}
                onClick={handleSendSelectedModel}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Selection Modal */}
      {showVoiceModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="modal-header-indicator" />
            <h3>Choose Your AI Model Voice</h3>
            <ul className="voice-list">
              {[
                "Nova – Female, Soft",
                "Alloy – Neutral",
                "Echo – Male",
                "Fable – Female",
                "Onyx – Male, Deep",
                "Shimmer – Female, Clear",
              ].map((voice, index) => (
                <li
                  style={{
                    backgroundColor: selectedVoice === voice ? "#dfefff" : "",
                    cursor: "pointer",
                    borderRadius: "8px",
                    padding: "10px",
                    transition: "0.2s",
                  }}
                  key={index}
                  className="voice-item"
                  onClick={(index) => handleSetVoice(index)}
                >
                  <span>{voice}</span>
                </li>
              ))}
            </ul>
            <div className="modal-actions">
              <button
                className="cancel"
                onClick={() => setShowVoiceModal(false)}
              >
                Cancel
              </button>
              <button
                className="done"
                disabled={!selectedVoice}
                onClick={handleVoiceSelect}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {/* End Call Modal */}
      {showEndModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="modal-header-indicator" />
            <h3>Call Ends In 10</h3>
            <div className="modal-actions">
              <button className="cancel" onClick={() => setShowEndModal(false)}>
                Cancel
              </button>
              <button
                className="done"
                onClick={handleEndNow}
                style={{
                  background: "#ff4d4f",
                  color: "white",
                  border: "none",
                }}
              >
                End Now
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Video Confirmation Modal */}
      {showVideoConfirmModal && (
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="modal-header-indicator" />
            <h3>Confirmation</h3>
            <p>
              To start a video session with <strong>{selectedExpert}</strong>?
            </p>
            <div className="modal-actions">
              <button
                className="cancel"
                onClick={() => setShowVideoConfirmModal(false)}
              >
                Stay in Audio
              </button>
              <button className="done" onClick={() => navigate("/vsession")}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionPage;
